import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { notifyThirdPartyCartScripts } from '@theme/section-renderer';

/** Releasit COD and similar apps inject buy-now buttons into the cart drawer. */
const COD_TRIGGER_SELECTOR =
  '#_rsi-buy-now-button, .cart__app-extra-buttons [id^="_rsi"], .cart__app-extra-buttons [class*="_rsi"]';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | undefined} */
  #codModalObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#notifyCodCompat);
    this.addEventListener('click', this.#handleCodTriggerClick, true);
    this.#watchCodModalOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#notifyCodCompat);
    this.removeEventListener('click', this.#handleCodTriggerClick, true);
    this.#codModalObserver?.disconnect();
    this.#codModalObserver = undefined;
  }

  /** Some checkout apps lazy-mount only when the drawer becomes visible after morph emptied their nodes. */
  #notifyCodCompat = () => {
    notifyThirdPartyCartScripts(this);
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /** Close the drawer when a COD / one-click order button inside the cart is clicked. */
  #handleCodTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(COD_TRIGGER_SELECTOR);
    if (!trigger || !this.contains(trigger)) return;

    requestAnimationFrame(() => this.closeDialog());
  };

  /** Close the drawer when Releasit (or similar) mounts its form overlay on the page. */
  #watchCodModalOpen = () => {
    this.#codModalObserver?.disconnect();

    this.#codModalObserver = new MutationObserver((mutations) => {
      if (!this.refs.dialog?.open) return;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (this.#isCodOverlay(node)) {
            this.closeDialog();
            return;
          }
        }
      }
    });

    this.#codModalObserver.observe(document.body, { childList: true });
  };

  /** @param {HTMLElement} node */
  #isCodOverlay(node) {
    const id = node.id ?? '';
    const className = typeof node.className === 'string' ? node.className : '';

    if (!id.startsWith('_rsi') && !className.includes('_rsi')) return false;

    return (
      id.includes('modal') ||
      id.includes('overlay') ||
      id.includes('form') ||
      className.includes('modal') ||
      className.includes('overlay') ||
      className.includes('cod-form')
    );
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
